// On-device numbers, POSTed home while layout/mic bugs resist remote
// reasoning. The companion shell calls postPhoneDiag() on mount when
// running as a home-screen app; the More screen's Diagnostics block uses
// collectDiagSnapshot() for the on-device readout. Pure measurement —
// the microphone is only ever READ passively (Permissions API), never
// requested from here (a request needs the tap's gesture).

export type DiagSnapshot = Record<string, string>;

function isStandalone(): boolean {
  // iOS Safari home-screen flag; lib.dom's Navigator doesn't type it.
  const navWithFlag: Navigator & { standalone?: boolean } = navigator;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    navWithFlag.standalone === true
  );
}

export function collectDiagSnapshot(): DiagSnapshot {
  const vv = window.visualViewport;
  const probe = (h: string) => {
    const d = document.createElement("div");
    d.style.cssText = `position:fixed;left:-10px;top:0;width:1px;height:${h};visibility:hidden;pointer-events:none;`;
    document.body.appendChild(d);
    const px = Math.round(d.getBoundingClientRect().height);
    d.remove();
    return Number.isFinite(px) ? String(px) : "n/a";
  };
  const envProbe = document.createElement("div");
  envProbe.style.cssText =
    "position:fixed;left:-10px;top:0;width:1px;height:1px;visibility:hidden;pointer-events:none;" +
    "padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);" +
    "padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px);";
  document.body.appendChild(envProbe);
  const cs = getComputedStyle(envProbe);
  const env = [cs.paddingTop, cs.paddingBottom, cs.paddingLeft, cs.paddingRight].map((v) => parseFloat(v) || 0).join("/");
  envProbe.remove();
  // Where the shell and the tab bar ACTUALLY landed, which is the number
  // that separates "the viewport is short" from "the bar pads itself".
  const nav = document.querySelector("[data-companion-nav]");
  const navBottom = nav ? Math.round(nav.getBoundingClientRect().bottom) : null;
  return {
    build: typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev",
    UA: navigator.userAgent.match(/OS [\d_]+/)?.[0] ?? navigator.userAgent.slice(0, 60),
    mode: isStandalone() ? "standalone" : "browser",
    sw: navigator.serviceWorker?.controller ? "controlling" : "none",
    inner: String(window.innerHeight),
    vv: vv ? `${Math.round(vv.height)} @${Math.round(vv.offsetTop)}` : "none",
    client: String(document.documentElement.clientHeight),
    screen: `${screen.height}@${window.devicePixelRatio}x`,
    units: `${probe("100vh")}/${probe("100svh")}/${probe("100dvh")}/${probe("100lvh")}`,
    dvh: CSS.supports?.("height", "100dvh") ? "yes" : "NO",
    env,
    appH: getComputedStyle(document.documentElement).getPropertyValue("--app-h").trim() || "css",
    navB: navBottom === null ? "none" : String(navBottom),
    mic: typeof navigator.mediaDevices?.getUserMedia === "function" ? (window.MediaRecorder?.isTypeSupported?.("audio/mp4") ? "mp4" : "gUM") : "MISSING",
  };
}

/** The sync snapshot plus the passive microphone permission state. */
export async function collectDiagSnapshotFull(): Promise<DiagSnapshot> {
  const s = collectDiagSnapshot();
  try {
    const perms = navigator.permissions;
    if (perms?.query) {
      const p = await perms.query({ name: "microphone" });
      s.micPerm = p.state;
    } else {
      s.micPerm = "api-missing";
    }
  } catch {
    s.micPerm = "n/a";
  }
  return s;
}

/** POSTs a snapshot to the backend's log sink at a small ladder of
 * delays — launch-time viewport misreports converge over the first
 * seconds, and we want to see both the lie and the settled truth.
 * Returns a cancel function. */
export function postPhoneDiag(): () => void {
  if (!isStandalone()) return () => undefined;
  const timers = [700, 2000, 5000, 12000].map((ms) =>
    window.setTimeout(() => {
      void collectDiagSnapshotFull()
        .then((snap) =>
          fetch("/api/diag", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ ...snap, path: location.pathname, ts: new Date().toISOString() }),
          }),
        )
        .catch(() => undefined);
    }, ms),
  );
  return () => timers.forEach((t) => window.clearTimeout(t));
}
